import { Col, Input, Radio, Row, Select, Table } from "antd";
import {
  fetchVolComparisonShortcuts,
  previewVolComparison,
  sendVolComparison,
} from "api/volSurfaceApi";
import FloatingPanel from "components/Common/FloatingPanel/FloatingPanel";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import { toast } from "helpers/generalHelper";
import {
  buildVolComparisonRowsPayload,
  MIN_ACCUMULATED_QTY_MODES,
  normalizeMinAccumulatedQtyMode,
  prepareVolComparisonShortcuts,
} from "helpers/VolSurface/volComparisonShortcuts";
import { trackVolSurfaceComparison } from "helpers/googleAnalyticsHelper";
import { useCallback, useEffect, useState } from "react";
import {
  CancelButton,
  PrimaryButton,
  SecondaryButton,
} from "../CustomButtons/CustomButtons";
import VolComparisonMinAccumulatedQtyModal from "./VolComparisonMinAccumulatedQtyModal";
import "./VolSurfaceComparison.scss";

const { TextArea } = Input;

const STEP_SELECT = "select";
const STEP_PREVIEW = "preview";

const PANEL_OPTIONS = {
  widthRatio: 0.62,
  heightRatio: 0.72,
  minWidth: 720,
  minHeight: 420,
  margin: 16,
};

export default function VolSurfaceComparison({
  dark_Theme,
  onOpen3dSurface,
  show3dButton = false,
  variant = "inline",
  open,
  onClose,
}) {
  const isControlled = open !== undefined;
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(STEP_SELECT);
  const [shortcuts, setShortcuts] = useState([]);
  const [selectedShortcuts, setSelectedShortcuts] = useState([]);
  const [isShortcutsLoading, setIsShortcutsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);
  const [body, setBody] = useState("");
  const [subject, setSubject] = useState("");
  const [editShortcut, setEditShortcut] = useState(null);

  const panelOpen = isControlled ? open : showModal;
  const fromPricer = variant === "none";

  const buildRowsPayload = () =>
    buildVolComparisonRowsPayload(selectedShortcuts);

  const missingComparison = selectedShortcuts.some(
    (item) => !item?.comparison_surface_id
  );

  const getShortcuts = useCallback(() => {
    setIsShortcutsLoading(true);
    fetchVolComparisonShortcuts()
      .then((res) => {
        const { shortcuts: rows, selectedShortcuts: selected } =
          prepareVolComparisonShortcuts(res.data?.data || []);
        setShortcuts(rows);
        setSelectedShortcuts(selected);
      })
      .finally(() => {
        setIsShortcutsLoading(false);
      });
  }, []);

  const resetTransientState = () => {
    setStep(STEP_SELECT);
    setSelectedShortcuts([]);
    setShortcuts([]);
    setBody("");
    setSubject("");
    setEditShortcut(null);
  };

  const handleClose = () => {
    resetTransientState();
    if (isControlled) {
      onClose?.();
      return;
    }
    setShowModal(false);
  };

  const handleOpen = () => {
    trackVolSurfaceComparison("open", { fromPricer });
    setStep(STEP_SELECT);
    getShortcuts();
    if (!isControlled) {
      setShowModal(true);
    }
  };

  useEffect(() => {
    if (!isControlled || !open) return;
    setStep(STEP_SELECT);
    getShortcuts();
  }, [isControlled, open, getShortcuts]);

  const handleSelectionChange = (selectedRows) => {
    setSelectedShortcuts(selectedRows);
  };

  const updateShortcutRow = (productContractStyleId, patch) => {
    const updateRow = (row) =>
      row.product_contract_style_id === productContractStyleId
        ? { ...row, ...patch }
        : row;

    setShortcuts((prev) => prev.map(updateRow));
    setSelectedShortcuts((prev) => prev.map(updateRow));
  };

  const handleComparisonChange = (productContractStyleId, surfaceId) => {
    updateShortcutRow(productContractStyleId, {
      comparison_surface_id: surfaceId ?? null,
    });
  };

  const handleMinQtyModeChange = (productContractStyleId, mode) => {
    updateShortcutRow(productContractStyleId, {
      min_accumulated_qty_mode: normalizeMinAccumulatedQtyMode(mode),
    });
  };

  const handlePreview = () => {
    setIsPreviewLoading(true);
    previewVolComparison({ rows: buildRowsPayload() })
      .then((res) => {
        if (res.status === 200) {
          const data = res?.data?.data;
          setBody(data?.body || "");
          setSubject(data?.subject || "");
          setStep(STEP_PREVIEW);
          trackVolSurfaceComparison("preview", {
            fromPricer,
            extraParams: { row_count: selectedShortcuts.length },
          });
        } else {
          toast(res?.data?.message || "Failed to build preview", "warning");
        }
      })
      .finally(() => {
        setIsPreviewLoading(false);
      });
  };

  const handleSend = () => {
    setIsSendLoading(true);
    sendVolComparison({
      rows: buildRowsPayload(),
      body,
      subject: subject || undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          toast(
            res?.data?.message || "Vol surface comparison email sent.",
            "success"
          );
          trackVolSurfaceComparison("send", {
            fromPricer,
            extraParams: { row_count: selectedShortcuts.length },
          });
          setTimeout(() => {
            handleClose();
          }, 1000);
        } else {
          toast(res?.data?.message || "Failed to send email", "warning");
        }
      })
      .finally(() => {
        setIsSendLoading(false);
      });
  };

  const panelTitle =
    step === STEP_SELECT
      ? "Vol Surface Comparison"
      : "Vol Surface Comparison Preview";

  const footerButtons =
    step === STEP_SELECT
      ? [
          <CancelButton key="cancel" onClick={handleClose}>
            Cancel
          </CancelButton>,
          <PrimaryButton
            key="preview"
            loading={isPreviewLoading}
            disabled={
              isPreviewLoading ||
              selectedShortcuts.length === 0 ||
              missingComparison
            }
            onClick={handlePreview}
          >
            Preview
          </PrimaryButton>,
        ]
      : [
          <CancelButton key="cancel" onClick={handleClose}>
            Cancel
          </CancelButton>,
          <SecondaryButton
            key="back"
            disabled={isSendLoading}
            onClick={() => setStep(STEP_SELECT)}
          >
            Back
          </SecondaryButton>,
          <PrimaryButton
            key="send"
            loading={isSendLoading}
            disabled={isSendLoading || !body?.trim()}
            onClick={handleSend}
          >
            Send
          </PrimaryButton>,
        ];

  return (
    <>
      {variant === "inline" && (
        <Col span={24}>
          <Row className="bias-btn" gutter={[0, 8]}>
            <Col span={3}>
              Vol Surface
              <br />
              Comparison:
            </Col>
            <Col>
              <div className="vol-surface-comparison-controls">
                <SecondaryButton
                  onClick={() => {
                    handleOpen();
                  }}
                >
                  Run
                </SecondaryButton>
                {show3dButton && onOpen3dSurface && (
                  <div className="vol-surface-3d-trigger">
                    <PrimaryButton onClick={onOpen3dSurface}>
                      3D Surface
                    </PrimaryButton>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Col>
      )}

      <FloatingPanel
        open={panelOpen}
        onClose={handleClose}
        title={panelTitle}
        dark={dark_Theme}
        className={`vol-surface-comparison-panel${
          dark_Theme ? " content_dark" : ""
        }`}
        bodyClassName="vol-surface-comparison-panel__body"
        panelOptions={PANEL_OPTIONS}
      >
        <div className="vol-surface-comparison-panel__content">
          {isShortcutsLoading || isPreviewLoading ? (
            <PreLoadPagesSpin
              className="other-info--container--spin"
              size="large"
              dark_Theme={dark_Theme}
            />
          ) : step === STEP_SELECT ? (
            <>
              <p className="fw-bold fs-6">Select Shortcuts</p>
              <div className="vol-surface-comparison-panel__table">
                <Table
                  pagination={false}
                  bordered
                  size="small"
                  className={`${dark_Theme ? "table-dark" : ""}`}
                  rowKey={(record) => record?.product_contract_style_id}
                  dataSource={shortcuts.map((item) => ({
                    ...item,
                    key: item?.product_contract_style_id,
                  }))}
                  rowSelection={{
                    selectedRowKeys: selectedShortcuts.map(
                      (item) => item?.product_contract_style_id
                    ),
                    type: "checkbox",
                    onChange: (_selectedRowKeys, selectedRows) => {
                      handleSelectionChange(selectedRows);
                    },
                  }}
                  columns={[
                    {
                      title: "Shortcut",
                      dataIndex: "label",
                      key: "label",
                      width: "22%",
                    },
                    {
                      title: "Live Surface",
                      dataIndex: "live_surface_title",
                      key: "live_surface_title",
                      width: "18%",
                      render: (title) => title || "—",
                    },
                    {
                      title: "Comparison Surface",
                      dataIndex: "comparison_surface_id",
                      key: "comparison_surface_id",
                      width: "28%",
                      render: (value, record) => (
                        <div
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Select
                            allowClear
                            showSearch
                            placeholder="Select comparison"
                            style={{ width: "100%" }}
                            className={dark_Theme ? "select-dark" : ""}
                            dropdownClassName={`vol-surface-comparison-dropdown${
                              dark_Theme ? " dropdown-dark" : ""
                            }`}
                            popupClassName={`vol-surface-comparison-dropdown${
                              dark_Theme ? " dropdown-dark" : ""
                            }`}
                            dropdownStyle={{ zIndex: 2200 }}
                            getPopupContainer={() => document.body}
                            virtual={false}
                            value={value ?? null}
                            options={(record.comparison_surfaces || []).map(
                              (surface) => ({
                                value: surface.id,
                                label: surface.title,
                              })
                            )}
                            filterOption={(input, option) =>
                              (option?.label || "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            onChange={(surfaceId) =>
                              handleComparisonChange(
                                record.product_contract_style_id,
                                surfaceId
                              )
                            }
                          />
                        </div>
                      ),
                    },
                    {
                      title: "Min Accumulated Qty",
                      key: "min_accumulated_qty_mode",
                      width: "32%",
                      render: (_value, record) => (
                        <div
                          className="vol-surface-comparison-min-qty"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Radio.Group
                            value={normalizeMinAccumulatedQtyMode(
                              record.min_accumulated_qty_mode
                            )}
                            onChange={(e) =>
                              handleMinQtyModeChange(
                                record.product_contract_style_id,
                                e.target.value
                              )
                            }
                          >
                            <Radio value={MIN_ACCUMULATED_QTY_MODES.ALL}>
                              All
                            </Radio>
                            <Radio value={MIN_ACCUMULATED_QTY_MODES.CUSTOM}>
                              Custom
                            </Radio>
                          </Radio.Group>
                          <SecondaryButton
                            size="small"
                            onClick={() => setEditShortcut(record)}
                          >
                            Edit
                          </SecondaryButton>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </>
          ) : (
            <div className="vol-surface-comparison-panel__preview">
              <div>
                <p className="fw-bold mb-1">Subject</p>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div className="vol-surface-comparison-panel__body-field">
                <p className="fw-bold mb-1">Body</p>
                <div className="vol-surface-comparison-panel__textarea-wrap">
                  <TextArea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="vol-surface-comparison-panel__textarea"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="vol-surface-comparison-panel__footer">
          {footerButtons}
        </div>
      </FloatingPanel>

      <VolComparisonMinAccumulatedQtyModal
        open={!!editShortcut}
        dark_Theme={dark_Theme}
        productContractStyleId={editShortcut?.product_contract_style_id}
        title={editShortcut?.label}
        onClose={() => setEditShortcut(null)}
      />
    </>
  );
}
